/**
 * The grace a paying member keeps after a renewal charge is declined.
 *
 * Until 2026-10-19 the first declined renewal switched every perk off, before
 * Paddle had even retried the card, so a member with an expired card lost
 * Elite mid-month for what is usually fixed on the next retry. Now a member
 * who has paid before keeps the perks for 7 days from the first failed
 * renewal while Paddle retries. The perks go off when Paddle cancels the
 * subscription (subscription.canceled), or when 7 days pass and it is still
 * past_due: the daily membership sweep in functions/ (autoCancelPastDueMemberships)
 * enforces that end (functions/src/membership-reconcile-rules.ts mirrors the length;
 * paddle-payment-grace.test.ts keeps them equal).
 *
 * The grace start is kept server-only, on users/{uid}/paddle_state/{subId}
 * (see paddle-event-order.ts), never on the user doc. A Paddle trial whose
 * conversion charge fails gets no grace: that card never paid, and every
 * Paddle trial so far ended exactly that way.
 */

export const PAYMENT_GRACE_DAYS = 7;
export const PAYMENT_GRACE_MS = PAYMENT_GRACE_DAYS * 24 * 60 * 60 * 1000;

export type PaymentGraceAccount = {
    subscriptionStatus?: unknown;
    isPremium?: unknown;
    isLite?: unknown;
    membershipNanoCoresGranted?: unknown;
    totalSpentUSD?: unknown;
};

/** An ISO string, millis, Date or Timestamp as millis, or null. */
export function graceTimeMs(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const anyValue = value as any;
    if (typeof anyValue?.toMillis === 'function') return anyValue.toMillis();
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
    if (typeof value === 'string' || typeof value === 'number') {
        const ms = typeof value === 'number' ? value : Date.parse(value);
        return Number.isFinite(ms) ? ms : null;
    }
    return null;
}

/**
 * Whether the account ever paid for a membership. The starter cores are
 * granted once, on the first paid membership charge; Lite members from before
 * 2026-09-24 never got them, so money spent counts too.
 */
export function hasPaidForMembership(account: PaymentGraceAccount): boolean {
    return account.membershipNanoCoresGranted === true || Number(account.totalSpentUSD) > 0;
}

export type PaymentGracePlan = {
    /** Keep isPremium / isLite as they are while the subscription is past_due. */
    keepPerks: boolean;
    /** The grace start to store (ISO), or null when there is no grace. */
    graceStartedAt: string | null;
    graceEndsAtMs: number | null;
    reason: 'in_grace' | 'grace_started' | 'grace_over' | 'no_perks' | 'trial' | 'never_paid';
};

/**
 * What a past_due subscription means for the account right now.
 * `account` is the user doc as it was BEFORE this payment failure.
 */
export function planPaymentGrace(input: {
    account: PaymentGraceAccount;
    graceStartedAt: unknown;
    nowMs: number;
}): PaymentGracePlan {
    const startedMs = graceTimeMs(input.graceStartedAt);
    if (startedMs !== null) {
        const endsAtMs = startedMs + PAYMENT_GRACE_MS;
        const graceStartedAt = new Date(startedMs).toISOString();
        return input.nowMs < endsAtMs
            ? { keepPerks: true, graceStartedAt, graceEndsAtMs: endsAtMs, reason: 'in_grace' }
            : { keepPerks: false, graceStartedAt, graceEndsAtMs: endsAtMs, reason: 'grace_over' };
    }
    const account = input.account;
    const hasPerks = account.isPremium === true || account.isLite === true;
    if (!hasPerks) return { keepPerks: false, graceStartedAt: null, graceEndsAtMs: null, reason: 'no_perks' };
    // Only a running paid plan. 'trialing' (and the 'active' Paddle shows for
    // a moment at trial end, before it charges) is a trial conversion.
    if (account.subscriptionStatus !== 'active' && account.subscriptionStatus !== 'past_due') {
        return { keepPerks: false, graceStartedAt: null, graceEndsAtMs: null, reason: 'trial' };
    }
    if (!hasPaidForMembership(account)) {
        return { keepPerks: false, graceStartedAt: null, graceEndsAtMs: null, reason: 'never_paid' };
    }
    return {
        keepPerks: true,
        graceStartedAt: new Date(input.nowMs).toISOString(),
        graceEndsAtMs: input.nowMs + PAYMENT_GRACE_MS,
        reason: 'grace_started',
    };
}

/**
 * What the browser can tell from the user doc alone: past_due with the perks
 * still on is a member inside the grace. The server switches isPremium /
 * isLite off when the grace ends, so the flags are the answer for past_due.
 */
export function hasPerksInPaymentGrace(user: { subscriptionStatus?: unknown; isPremium?: unknown; isLite?: unknown } | null | undefined): boolean {
    if (!user || user.subscriptionStatus !== 'past_due') return false;
    return user.isPremium === true || user.isLite === true;
}
